import * as React from 'react';
import { Info, MessageSquare, SendHorizontal, X } from 'lucide-react';
import { WorkspaceButton } from '@/components/ui/workspace';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils/cn';
import type { ChatChangeSet, ChatThread } from '@/lib/api/process-maps';

/**
 * "Change this map by chat" (T-0189, Decision 51), beside the canvas, for
 * engagement maps and reference processes. Prototype: `chatPanel()` and
 * `.mc-*` in `docs/discovery/prototype/`.
 *
 * The thread is the server's record (every prompt and change is kept). A
 * change request comes back as a change set checked by code: the newest
 * one still proposed is shown on the map with Apply and Discard; nothing
 * changes until Apply. Questions are answered by code from the map.
 */
export interface MapChatPanelProps {
  versionLabel: string;
  thread: ChatThread | undefined;
  error: unknown;
  intro: string;
  suggestions: string[];
  /** The input is off, with this reason (editing, no draft open, an older version shown). */
  disabledReason: string | null;
  sending: boolean;
  /** The change set shown on the map, which Apply and Discard act on. */
  activeId: string | null;
  busy: boolean;
  applyDisabledReason?: string | null;
  footNote?: React.ReactNode;
  undo?: { label: string; onUndo: () => void } | null;
  onSend: (text: string) => Promise<boolean>;
  onApply: (cs: ChatChangeSet) => void;
  onDiscard: (cs: ChatChangeSet) => void;
  onHide: () => void;
  /** Put the cursor in the input when the panel opens (the floating chat of the full-screen map). */
  autoFocus?: boolean;
}

export function MapChatPanel({
  versionLabel,
  thread,
  error,
  intro,
  suggestions,
  disabledReason,
  sending,
  activeId,
  busy,
  applyDisabledReason,
  footNote,
  undo,
  onSend,
  onApply,
  onDiscard,
  onHide,
  autoFocus = false,
}: MapChatPanelProps) {
  const [text, setText] = React.useState('');
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const messages = thread?.messages ?? [];
  const off = disabledReason !== null || sending;

  React.useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, sending]);

  const send = async (value: string) => {
    const t = value.trim();
    if (!t || off) return;
    if (await onSend(t)) setText('');
  };

  return (
    <aside
      role="complementary"
      aria-label="Change this map by chat"
      className="flex min-h-0 flex-1 flex-col bg-paper-0"
    >
      <div className="flex flex-none items-center gap-2.5 border-b border-ink-100 px-4 py-3">
        <MessageSquare className="h-4 w-4 flex-none text-ink-700" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-ink-900">
          Change this map by chat
        </span>
        <span className="text-meta text-ink-500">{versionLabel}</span>
        <button
          type="button"
          onClick={onHide}
          aria-label="Hide the chat"
          className="inline-flex h-[26px] w-[26px] flex-none items-center justify-center rounded-chip text-ink-500 hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div
        ref={bodyRef}
        data-testid="map-chat-thread"
        className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto bg-paper-50 p-3.5"
      >
        <Bubble mine={false}>{intro}</Bubble>
        {error ? <ErrorMessage error={error} /> : null}
        {!thread && !error ? (
          <div className="flex items-center gap-2 text-meta text-ink-500">
            <Spinner /> Loading the conversation…
          </div>
        ) : null}
        {messages.map((m) =>
          m.role === 'user' ? (
            <Bubble key={m.id} mine>
              {m.text}
            </Bubble>
          ) : (
            <Bubble key={m.id} mine={false}>
              <div className="whitespace-pre-line">{m.text}</div>
              {m.change_set ? (
                <ChangeSetState
                  cs={m.change_set}
                  active={m.change_set.id === activeId}
                  busy={busy}
                  applyDisabledReason={applyDisabledReason ?? null}
                  onApply={() => onApply(m.change_set!)}
                  onDiscard={() => onDiscard(m.change_set!)}
                />
              ) : null}
            </Bubble>
          ),
        )}
        {sending ? (
          <div className="flex items-center gap-2 text-meta text-ink-500">
            <Spinner /> Working it out…
          </div>
        ) : null}
      </div>

      <div className="flex flex-none flex-col gap-2 border-t border-ink-100 bg-paper-0 px-3.5 py-3">
        {messages.length === 0 && !disabledReason ? (
          <div className="flex flex-col gap-1.5">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                disabled={off}
                onClick={() => void send(s)}
                className="rounded-card border border-dashed border-ink-200 bg-paper-0 px-2.5 py-1.5 text-left text-meta text-ink-700 hover:border-ink-300 hover:text-ink-900 disabled:opacity-60"
              >
                {s}
              </button>
            ))}
          </div>
        ) : null}
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void send(text);
          }}
        >
          <input
            aria-label="Describe a change, or ask about the map"
            placeholder={disabledReason ?? 'Describe a change, or ask about the map'}
            value={text}
            disabled={off}
            maxLength={2000}
            autoComplete="off"
            autoFocus={autoFocus}
            onChange={(e) => setText(e.target.value)}
            className="h-9 min-w-0 flex-1 rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm text-ink-900 placeholder:text-ink-500 focus:border-ink-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 disabled:bg-paper-50"
          />
          <WorkspaceButton
            type="submit"
            variant="primary"
            size="md"
            aria-label="Send"
            disabled={off || text.trim() === ''}
          >
            <SendHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
          </WorkspaceButton>
        </form>
        {footNote ? (
          <p className="flex items-start gap-1.5 text-meta text-ink-500">
            <Info className="mt-[3px] h-3 w-3 flex-none" aria-hidden="true" />
            <span>{footNote}</span>
          </p>
        ) : null}
        {undo ? (
          <button
            type="button"
            disabled={busy}
            onClick={undo.onUndo}
            className="self-start text-meta font-medium text-ink-700 underline underline-offset-[3px] hover:text-ink-900 disabled:opacity-60"
          >
            {undo.label}
          </button>
        ) : null}
      </div>
    </aside>
  );
}

function Bubble({ mine, children }: { mine: boolean; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'max-w-[92%] rounded-card px-3 py-2.5 text-body-sm',
        mine
          ? 'self-end bg-ink-900 text-paper-0'
          : 'self-start border border-ink-100 bg-paper-0 text-ink-900',
      )}
    >
      {children}
    </div>
  );
}

function ChangeSetState({
  cs,
  active,
  busy,
  applyDisabledReason,
  onApply,
  onDiscard,
}: {
  cs: ChatChangeSet;
  active: boolean;
  busy: boolean;
  applyDisabledReason: string | null;
  onApply: () => void;
  onDiscard: () => void;
}) {
  if (!cs.valid) return null;
  if (cs.status === 'applied')
    return (
      <p className="mt-1.5 text-meta text-ink-500">
        {cs.applied_version_no ? `Applied as version ${cs.applied_version_no}.` : 'Applied.'}
      </p>
    );
  if (cs.status === 'discarded')
    return <p className="mt-1.5 text-meta text-ink-500">Discarded. The map is unchanged.</p>;
  if (cs.status === 'stale')
    return (
      <p className="mt-1.5 text-meta text-ink-500">
        The map changed before this was applied, so it can no longer be applied. Ask again.
      </p>
    );
  if (!active) return <p className="mt-1.5 text-meta text-ink-500">Not applied.</p>;
  return (
    <>
      <p className="mt-1.5 text-meta text-ink-500">Highlighted on the map.</p>
      <div className="mt-2 flex gap-2">
        <WorkspaceButton variant="tertiary" disabled={busy} onClick={onDiscard}>
          Discard
        </WorkspaceButton>
        <WorkspaceButton
          variant="primary"
          disabled={busy || applyDisabledReason !== null}
          title={applyDisabledReason ?? undefined}
          onClick={onApply}
        >
          Apply
        </WorkspaceButton>
      </div>
    </>
  );
}

/**
 * The newest change set in a thread that can still be applied: shown on the
 * map. One proposed before the version on screen was made is not shown: it
 * was made against an older map, and applying it would be refused.
 */
export function activeChatChangeSet(
  thread: ChatThread | undefined,
  since: string | null = null,
): ChatChangeSet | null {
  const all = (thread?.messages ?? []).flatMap((m) =>
    m.role === 'assistant' && m.change_set ? [{ cs: m.change_set, at: m.created_at }] : [],
  );
  const last = all[all.length - 1];
  if (!last || !last.cs.valid || last.cs.status !== 'proposed') return null;
  if (since && Date.parse(last.at) < Date.parse(since)) return null;
  return last.cs;
}
